import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';
import type {
  ResourceCategory,
  ResourceLink,
  SharedAsset,
  SharedAssetFolder,
} from './resourceLinks';

/**
 * Reads and writes for the Resource Directory and the Shared Asset Library.
 *
 * Every column is listed. PostgREST infers the row type from the select list,
 * and a `*` here would break the moment a column is added — the same trap
 * TASK_COLUMNS documents in the tasks feature.
 */

// prettier-ignore
const CATEGORY_COLUMNS = 'id, name, color_key, position, created_by, created_at, updated_at, parent_id';
// prettier-ignore
const LINK_COLUMNS = 'id, category_id, title, url, description, display_order, is_pinned, created_by, created_at, updated_at';
// prettier-ignore
const ASSET_COLUMNS =
  'id, name, url, drive_file_id, mime_type, size_bytes, uploaded_by, created_at, folder_id';
// prettier-ignore
const FOLDER_COLUMNS = 'id, name, drive_folder_id, created_by, created_at';

export const resourceKeys = {
  all: ['resources'] as const,
  categories: () => [...resourceKeys.all, 'categories'] as const,
  links: () => [...resourceKeys.all, 'links'] as const,
  assets: () => [...resourceKeys.all, 'assets'] as const,
  folders: () => [...resourceKeys.all, 'folders'] as const,
};

export async function fetchResourceCategories(client: SupabaseClient): Promise<ResourceCategory[]> {
  const { data, error } = await client
    .from('resource_categories')
    .select(CATEGORY_COLUMNS)
    .order('position', { ascending: true });
  if (error) {
    throw new Error(error.message);
  }
  return data ?? [];
}

export async function fetchResourceLinks(client: SupabaseClient): Promise<ResourceLink[]> {
  const { data, error } = await client
    .from('resource_links')
    .select(LINK_COLUMNS)
    .order('display_order', { ascending: true });
  if (error) {
    throw new Error(error.message);
  }
  return data ?? [];
}

export async function fetchSharedAssets(client: SupabaseClient): Promise<SharedAsset[]> {
  const { data, error } = await client
    .from('shared_assets')
    .select(ASSET_COLUMNS)
    .order('created_at', { ascending: false });
  if (error) {
    throw new Error(error.message);
  }
  return data ?? [];
}

export async function fetchSharedAssetFolders(
  client: SupabaseClient,
): Promise<SharedAssetFolder[]> {
  const { data, error } = await client
    .from('shared_asset_folders')
    .select(FOLDER_COLUMNS)
    .order('name', { ascending: true });
  if (error) {
    throw new Error(error.message);
  }
  return data ?? [];
}

export function useResourceCategories() {
  const { client } = useAuth();
  return useQuery({
    queryKey: resourceKeys.categories(),
    queryFn: () => fetchResourceCategories(client),
    retry: false,
  });
}

export function useResourceLinks() {
  const { client } = useAuth();
  return useQuery({
    queryKey: resourceKeys.links(),
    queryFn: () => fetchResourceLinks(client),
    retry: false,
  });
}

export function useSharedAssets() {
  const { client } = useAuth();
  return useQuery({
    queryKey: resourceKeys.assets(),
    queryFn: () => fetchSharedAssets(client),
    retry: false,
  });
}

export function useSharedAssetFolders() {
  const { client } = useAuth();
  return useQuery({
    queryKey: resourceKeys.folders(),
    queryFn: () => fetchSharedAssetFolders(client),
    retry: false,
  });
}

function useActorId(): string {
  const { access } = useAuth();
  return access.status === 'approved' ? access.profile.id : '';
}

export function useCreateResourceLink() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = useActorId();
  return useMutation({
    mutationFn: async (input: {
      title: string;
      url: string;
      categoryId: string | null;
      description: string;
      isPinned: boolean;
    }) => {
      const { error } = await client.from('resource_links').insert({
        title: input.title.trim(),
        url: input.url.trim(),
        category_id: input.categoryId,
        description: input.description.trim(),
        is_pinned: input.isPinned,
        created_by: createdBy,
      });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: resourceKeys.links() });
    },
  });
}

export function useDeleteResourceLink() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (linkId: string) => {
      const { error } = await client.from('resource_links').delete().eq('id', linkId);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: resourceKeys.links() });
    },
  });
}

export function useCreateResourceCategory() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = useActorId();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      colorKey: string;
      position: number;
      parentId?: string | null;
    }) => {
      const { error } = await client.from('resource_categories').insert({
        name: input.name.trim(),
        color_key: input.colorKey,
        position: input.position,
        // Omitted rather than sent as null when absent: under
        // exactOptionalPropertyTypes the generated Insert type distinguishes
        // the two, and the column default already means "top level".
        ...(input.parentId === undefined || input.parentId === null
          ? {}
          : { parent_id: input.parentId }),
        created_by: createdBy,
      });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: resourceKeys.categories() });
    },
  });
}

export function useRecordSharedAsset() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const uploadedBy = useActorId();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      url: string;
      driveFileId: string | null;
      mimeType: string | null;
      sizeBytes: number | null;
      folderId?: string | null;
    }) => {
      const { error } = await client.from('shared_assets').insert({
        name: input.name,
        url: input.url,
        drive_file_id: input.driveFileId,
        mime_type: input.mimeType,
        size_bytes: input.sizeBytes,
        uploaded_by: uploadedBy,
        ...(input.folderId === undefined ? {} : { folder_id: input.folderId }),
      });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: resourceKeys.assets() });
    },
  });
}

export function useCreateSharedAssetFolder() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = useActorId();
  return useMutation({
    mutationFn: async (input: { name: string; driveFolderId: string }) => {
      const { data, error } = await client
        .from('shared_asset_folders')
        .insert({
          name: input.name.trim(),
          drive_folder_id: input.driveFolderId,
          created_by: createdBy,
        })
        .select(FOLDER_COLUMNS)
        .single();
      if (error) {
        throw new Error(error.message);
      }
      return data;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: resourceKeys.folders() });
    },
  });
}

export function useMoveSharedAsset() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { assetId: string; folderId: string | null }) => {
      const { data, error } = await client
        .from('shared_assets')
        .update({ folder_id: input.folderId })
        .eq('id', input.assetId)
        .select('id');
      if (error) {
        throw new Error(error.message);
      }
      if (data === null || data.length === 0) {
        throw new Error('shared_asset_move_refused');
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: resourceKeys.assets() });
    },
  });
}

export function useDeleteSharedAssetFolder() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await client
        .from('shared_asset_folders')
        .delete()
        .eq('id', id)
        .select('id');
      if (error) {
        throw new Error(error.message);
      }
      if (data === null || data.length === 0) {
        throw new Error('shared_asset_folder_delete_refused');
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: resourceKeys.all });
    },
  });
}

/**
 * Take a shared asset off the list. The Hub row only: the file stays in the
 * "_TAP Shared Assets" Drive folder, so a mistake can be undone from Drive.
 */
export function useDeleteSharedAsset() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (assetId: string) => {
      const { error } = await client.from('shared_assets').delete().eq('id', assetId);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: resourceKeys.assets() });
    },
  });
}

export function resourceErrorMessage(raw: string): string {
  if (raw.includes('shared_asset_move_refused')) {
    return "You can only move files you uploaded. Ask a TAP admin to move someone else's.";
  }
  if (raw.includes('shared_asset_folder_delete_refused')) {
    return 'Only a TAP admin can delete a folder.';
  }
  if (raw.includes('shared_asset_folders_name_idx')) {
    return 'A folder with that name already exists.';
  }
  if (raw.includes('shared_asset_folders_name_present')) {
    return 'Give the folder a name.';
  }
  if (raw.includes('resource_links_url_absolute')) {
    return 'A link needs to start with http:// or https://.';
  }
  if (raw.includes('resource_links_title_present') || raw.includes('categories_name_present')) {
    return 'Give it a name first.';
  }
  if (raw.includes('row-level security') || raw.includes('violates row-level')) {
    return 'You can only edit links you added. Ask an admin to change someone else’s.';
  }
  return 'That could not be saved. Try again.';
}

/**
 * Rename a section, or move it under a different one.
 *
 * Both go through one mutation because they are one row and one refetch. The
 * database refuses a name that collides with a sibling
 * (resource_categories_root_name_idx / _child_name_idx) and refuses a parent
 * that would close a loop (resource_categories_guard_cycle); this only has to
 * send them.
 */
export function useUpdateResourceCategory() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; name?: string; parentId?: string | null }) => {
      const changes: { name?: string; parent_id?: string | null } = {};
      if (input.name !== undefined) {
        changes.name = input.name.trim();
      }
      if (input.parentId !== undefined) {
        changes.parent_id = input.parentId;
      }
      if (Object.keys(changes).length === 0) {
        return;
      }
      const { error } = await client.from('resource_categories').update(changes).eq('id', input.id);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: resourceKeys.categories() });
    },
  });
}

/**
 * Delete a section.
 *
 * Its subsections go with it (parent_id is on delete cascade). Its LINKS do
 * not — resource_links.category_id is on delete set null, so they fall into
 * Uncategorised. That asymmetry is deliberate and the interface has to say it
 * out loud before anybody presses this.
 */
export function useDeleteResourceCategory() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await client.from('resource_categories').delete().eq('id', id);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: resourceKeys.all });
    },
  });
}

/** Clear the demo seed. Returns how many sections went. TAP admin only. */
export function useClearSeededCategories() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await client.rpc('clear_seeded_resource_categories');
      if (error) {
        throw new Error(error.message);
      }
      return data ?? 0;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: resourceKeys.all });
    },
  });
}
